// SEN-433 T9 (spec §6.3): the Dashboard's read-ahead, where Sentinel started its dashboard preload (at idle after
// app start, and after login). Poiesis's prefetchDashboard warms the tenant's default dashboard under the very keys
// the page asks, for the period the page will open on — the stored selection, or FY to date — so the page lands
// without a request of its own. Loaded on demand: none of this is in the first page's chunk.
import { fiscalYearToDate, toLocalDateString } from '@/lib/local-date'
import { activeTenantSlug } from './nomos/client'

const STORAGE_KEY = 'sentinel_time_period'

/** The inclusive local dates the time-period context opens on (its stored selection, else FY to date). */
function openingDates(fiscalMonth: number, zone: string): { periodKey: string; start_date: string; end_date: string } {
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? 'null') as { periodKey?: string; start?: string; end?: string } | null
    if (stored?.periodKey && stored.periodKey !== 'fytd' && stored.start && stored.end) {
      return { periodKey: stored.periodKey, start_date: toLocalDateString(new Date(stored.start)), end_date: toLocalDateString(new Date(stored.end)) }
    }
  } catch { /* storage blocked: the default */ }
  const { start, end } = fiscalYearToDate(fiscalMonth, new Date(), zone)
  return { periodKey: 'fytd', start_date: toLocalDateString(start), end_date: toLocalDateString(end) }
}

let started: Promise<string | null> | null = null

/** Warm the default dashboard once per session (a second call returns the first one's promise). */
export function prefetchDefaultDashboard(): Promise<string | null> {
  if (started) return started
  started = (async () => {
    const [{ tenantZone }, { listPeriodWindow, periodLabel }] = await Promise.all([
      import('./nomos/tz'), import('@/lib/useListPeriod')])
    const zone = await tenantZone()              // also sets Poiesis's tenant zone and fiscal month (one seam)
    const [{ prefetchDashboard }, { poiesisQueryClient }, { getTenantFiscalMonth }] = await Promise.all([
      import('@poiesis/dashboards/prefetchDashboard'), import('@/pages/work/poiesisHost'), import('@poiesis/locale'),
    ])
    const dates = openingDates(getTenantFiscalMonth() ?? 1, zone)
    // one react-query at runtime (vite dedupes it); the two packages' typings differ only by their copy
    const client = poiesisQueryClient as unknown as Parameters<typeof prefetchDashboard>[0]
    return prefetchDashboard(client, activeTenantSlug(),
      { ...listPeriodWindow(dates, zone), label: periodLabel(dates.periodKey) })
  })().catch(() => null)
  return started
}

/** A session change starts over (tests, sign-out). */
export function resetDashboardPrefetch(): void { started = null }
